import {
  NftOrderV4,
  SignatureStruct,
  SignedNftOrderV4,
} from '@onepercentio/nft-swap-sdk'
import { firestore, functions, firestoreModern, auth } from '..'
import {
  TimestampedManagement,
  UserBoundDocument,
  UserSignedOrder,
} from '../firebase.types'
import { GalleryType } from 'core/logic/gallery/gallery.types'
import { AssetType, Sale } from 'core/logic/asset/asset.types'
import { storage } from 'core/modules/firebase'
import {
  and,
  collection,
  doc,
  getCountFromServer,
  getDocFromCache,
  getDocs,
  getDocsFromServer,
  limit,
  or,
  orderBy,
  query,
  QueryConstraint,
  QueryFieldFilterConstraint,
  startAfter,
  where,
} from '@firebase/firestore'
import { i18n } from 'translate/i18n'
import { TypeSenseConfig } from 'core/constants'
import { useDynamicMarketplaceOffers } from 'context/Marketplace'
import {
  FirestoreGraphFilter,
  MarketplaceFilter,
} from 'components/AssetFilters/AssetFilters.types'
import { OrderBase } from '@onepercentio/rarum-types/marketplace/model'
import { fetchAssets, fetchAssetsDocs } from './assets'
import { OPEN_TEXT_FIELD } from 'components/AssetFilters/AssetFilters.data'
import { SnapshotFactoryReturn } from '@onepercentio/one-ui/hooks/useFirestoreWatch'
import { Gallery } from '@onepercentio/rarum-types/galleries'
import { getKeysAtLevel } from 'core/helpers/utils'
import { retryGetDocs } from '../error-utility'

type MarketplaceUserOfferParams = [
  galleryId: string,
  userId: string,
  tokenId: AssetType['tokenId']
]

export const MARKETPLACE_ASSET_IMAGE_PATH = ([
  galleryId,
  userId,
  tokenId,
]: MarketplaceUserOfferParams) =>
  `/marketplace/${process.env.REACT_APP_TENANT_IDENTITY_ID}/${galleryId}/${userId}/${tokenId}`

export const uploadPossibleFile = (
  ...params: [...MarketplaceUserOfferParams, file: File]
) => {
  const file = params[3]
  const path = `${MARKETPLACE_ASSET_IMAGE_PATH(params as any)}/${Date.now()}-${
    file.name
  }`
  return storage.ref(path).put(file)
}
export const listImages = (...args: MarketplaceUserOfferParams) =>
  storage.ref(MARKETPLACE_ASSET_IMAGE_PATH(args)).list()
export const removeImage = (ref: string) => storage.ref(ref).delete()

export async function write0xOrder(
  order: SignedNftOrderV4,
  asset: Pick<AssetType, 'galleryId'>
) {
  const response = await functions.httpsCallable(`marketplace-saveSellOrder`)({
    ...order,
    galleryId: asset.galleryId!,
  })
  return response.data as { transactionId: string }
}

type FirebaseOfferType = Pick<OrderBase, 'fulfillment'> & {
  order0x: NftOrderV4
  signedOrder: SignatureStruct
  orderParams: {
    asset: {
      cid?: string
    }
    description: string
  }
  orderRequest: {
    description: string
  }
  userId: string
} & TimestampedManagement & {
    galleryId: string
  }

export async function getMarketplaceOrders(galleryId: GalleryType['id']) {
  const offers = await firestore
    .collection(
      `/tenants/${process.env.REACT_APP_TENANT_IDENTITY_ID}/marketplace`
    )
    .where('galleryId', '==', galleryId)
    .where('status', '==', 'ACTIVE')
    .where('order0x.expiry', '>=', Number((Date.now() / 1000).toFixed(0)))
    .orderBy('created', 'asc')
    .get()
  return offers.docs.map((a) => ({
    ...(a.data() as FirebaseOfferType),
    id: a.id,
  }))
}

export function getMarketplaceOrdersQuery(
  galleryId: string,
  filter: Parameters<typeof useDynamicMarketplaceOffers>[0]
) {
  const openText = filter.open?.trim()
  const asMarketplaceFilter = filter as MarketplaceFilter
  const byStatus = asMarketplaceFilter.sold ? ['ACTIVE', 'LOCKED'] : ['ACTIVE']
  const whereConstraints: QueryFieldFilterConstraint[] = [
    where('status', 'in', byStatus),
  ]
  const traits = filter.trait || {}
  if (asMarketplaceFilter.sold) {
    whereConstraints.push(where('fulfillment', '!=', null))
  } else whereConstraints.push(where('fulfillment', '==', null))
  if (filter.byUser) {
    if (asMarketplaceFilter.sold)
      whereConstraints.push(
        or(
          where('userId', '==', auth.currentUser!.uid),
          where('fulfillment.buyerId', '==', auth.currentUser!.uid)
        ) as any
      )
    else whereConstraints.push(where('userId', '==', auth.currentUser!.uid))
  }
  for (let traitKey in traits)
    whereConstraints.push(
      where(`orderParams.attributes.${traitKey}`, '==', filter.trait![traitKey])
    )

  let openTextAssetNames: string[] | undefined = undefined

  const getBaseQuery = () => {
    const relevantConstraints = [...whereConstraints] as any[]
    if (openTextAssetNames)
      relevantConstraints.push(
        where(
          `orderParams.attributes.${OPEN_TEXT_FIELD}`,
          'in',
          openTextAssetNames
        ) as any
      )
    return query(
      collection(
        firestoreModern,
        `/tenants/${process.env.REACT_APP_TENANT_IDENTITY_ID}/marketplace`
      ),
      ...((relevantConstraints.length
        ? [and(...relevantConstraints)]
        : []) as any),
      orderBy('created')
    )
  }

  const hasOpenTextFilterFilter = async () => {
    if (!openText) return false
    if (openText && !openTextAssetNames) {
      const assetsInfo = await (async () => {
        try {
          const filterCache = await firestore
            .collection(
              `/tenants/${process.env.REACT_APP_TENANT_IDENTITY_ID}/galleries/${galleryId}/filters`
            )
            .doc('marketplace')
            .get({ source: 'cache' })
          const graphInfo = filterCache.data() as FirestoreGraphFilter
          const indexOfOpenText = graphInfo.attributes.indexOf(OPEN_TEXT_FIELD!)
          const possibleValues = getKeysAtLevel(
            graphInfo.graph,
            indexOfOpenText + 1
          )
          return possibleValues.map((a, i) => ({
            name: a,
            id: i,
          }))
        } catch (e) {
          return (await fetchAssetsDocs(true)).map((a, i) => ({
            ...a[i18n.language],
            id: i,
          }))
        }
      })()
      const MiniSearch = (await import('minisearch')).default
      let miniSearch = new MiniSearch({
        fields: ['name'], // fields to index for full-text search
        storeFields: ['name'], // fields to return with search results
      })
      miniSearch.addAll(assetsInfo)
      const relevantAssetsInfo = miniSearch.search(openText, { prefix: true })
      const relevantAssetNames = relevantAssetsInfo.map((a) => a.name)
      openTextAssetNames = relevantAssetNames
    }
    return !!openTextAssetNames!.length
  }

  return {
    docs: async (
      _page: number,
      size: number | 'all',
      afterId?: Sale['offerId']
    ) => {
      const contraints: QueryConstraint[] = [
        limit(size === 'all' ? 1000 : size),
      ]
      if (afterId)
        contraints.push(
          startAfter(
            await getDocFromCache(
              doc(
                firestoreModern,
                `/tenants/${process.env.REACT_APP_TENANT_IDENTITY_ID}/marketplace/${afterId}`
              )
            )
          )
        )
      if (openText && !(await hasOpenTextFilterFilter())) return []
      return retryGetDocs(async () =>
        (await getDocs(query(getBaseQuery(), ...contraints))).docs.map((a) => ({
          ...(a.data() as FirebaseOfferType),
          id: a.id,
        }))
      )
    },
    total: async () => {
      if (openText && !(await hasOpenTextFilterFilter())) return 0
      return (
        await retryGetDocs(async () => await getCountFromServer(getBaseQuery()))
      ).data().count
    },
  }
}

export async function getMarketplaceOrder(orderId: Sale['offerId']) {
  const offer = await firestore
    .doc(
      `/tenants/${process.env.REACT_APP_TENANT_IDENTITY_ID}/marketplace/${orderId}`
    )
    .get()
  return {
    ...(offer.data() as FirebaseOfferType),
    id: offer.id,
  }
}

export async function createCustodyOffer(
  selectedQuantity: number,
  tokenId: string,
  cid: string | undefined,
  amount: {
    [token: string]: {
      checked: boolean
      amount: number
    }
  },
  deadline: 'one_month' | 'two_months' | 'three_months',
  tenantId: string,
  galleryId: string,
  description: string | undefined,
  assetId: string
) {
  for (let currencySymbol in amount) {
    const lcCurrencySymbol = currencySymbol.toLowerCase()
    if (lcCurrencySymbol === currencySymbol) continue

    amount[lcCurrencySymbol] = amount[currencySymbol]
    delete amount[currencySymbol]
  }
  const response = await functions.httpsCallable(`marketplace-createSellOrder`)(
    {
      selectedQuantity,
      tokenId,
      amount,
      deadline,
      tenantId,
      galleryId,
      description,
      cid,
      assetId,
    }
  )
  return response.data as { id: string }
}

export async function disableOrder(galleryId: string, orderId: string) {
  await functions.httpsCallable('marketplace-cancelOrder')({
    tenantId: process.env.REACT_APP_TENANT_IDENTITY_ID,
    galleryId,
    orderId,
  })
}
export function getFilterSnapshot(
  galleryId: Gallery['id'],
  filterType: 'marketplace'
): SnapshotFactoryReturn<FirestoreGraphFilter & { id: string }> {
  if (!galleryId) return () => () => {}
  return (cb) => {
    return firestore
      .collection(
        `/tenants/${process.env.REACT_APP_TENANT_IDENTITY_ID}/galleries/${galleryId}/filters`
      )
      .doc(filterType)
      .onSnapshot((snap) => {
        cb([
          {
            doc: { ...(snap.data() as FirestoreGraphFilter), id: snap.id },
            type: undefined,
          },
        ])
      })
  }
}
